/**
 * MapView Component
 * Displays a Google Map with markers for all places of interest
 *
 * Uses the reusable map component following SOLID principles
 */

import React, { useMemo } from 'react';
import { ReusableMap, MapMarker } from '@/components/common/map';

export interface MapPlace {
  id: string;
  title: string;
  latitude: number;
  longitude: number;
  address?: string;
  image?: string;
  rating?: number;
  types?: string[]; // Place types from Google Places API
  experienceType?: string; // 'Package', 'Activity', 'Stay', etc.
  isExperience?: boolean; // Flag to identify experience markers
  price?: string; // Price display text (e.g., "₹23000.75")
}

export interface MapViewProps {
  places: MapPlace[];
  onPlaceClick?: (placeId: string) => void;
  mapId?: string; // Optional Map ID override if env var not working
}

export const MapView: React.FC<MapViewProps> = ({
  places,
  onPlaceClick,
  mapId,
}) => {
  // Convert MapPlace[] to MapMarker[]
  const markers: MapMarker[] = useMemo(() => {
    const mapped = places
      .filter(place => {
        // Additional validation
        return (
          place &&
          typeof place.latitude === 'number' &&
          typeof place.longitude === 'number' &&
          !isNaN(place.latitude) &&
          !isNaN(place.longitude) &&
          place.latitude >= -90 &&
          place.latitude <= 90 &&
          place.longitude >= -180 &&
          place.longitude <= 180
        );
      })
      .map(place => ({
        id: place.id || `marker_${Date.now()}_${Math.random()}`,
        title: place.title || 'Unknown Location',
        latitude: Number(place.latitude),
        longitude: Number(place.longitude),
        address: place.address,
        image: place.image,
        rating: place.rating,
        types: place.types,
        experienceType: place.experienceType,
        isExperience: place.isExperience,
        price: place.price,
      }));

    return mapped;
  }, [places]);

  // Handle marker click
  const handleMarkerClick = (marker: MapMarker) => {
    // Call onPlaceClick for both experience and location markers
    // This will trigger place details to open for locations
    onPlaceClick?.(marker.id);
  };

  return (
    <ReusableMap
      markers={markers}
      onMarkerClick={handleMarkerClick}
      autoFitBounds={true}
      boundsPadding={{ top: 80, right: 80, bottom: 80, left: 80 }}
      showOpenMapsButton={false}
      mapId={mapId}
    />
  );
};
